export class EncryptionManager {
  private static publicKey: CryptoKey | null = null;

  // Load PEM RSA public key into WebCrypto
  static async loadPublicKeyFromPem(pem: string) {
    const base64 = pem
      .replace('-----BEGIN PUBLIC KEY-----', '')
      .replace('-----END PUBLIC KEY-----', '')
      .replace(/\\n/g, '\n')
      .replace(/\s/g, '');

    const binaryDer = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));

    this.publicKey = await crypto.subtle.importKey(
      'spki',
      binaryDer,
      {
        name: 'RSA-OAEP',
        hash: 'SHA-256',
      },
      false,
      ['encrypt']
    );
  }

  // Encrypt string → Base64
  static async encryptString(text: string): Promise<string> {
    if (!this.publicKey) throw new Error('key not loaded');

    const encrypted = await crypto.subtle.encrypt(
      { name: 'RSA-OAEP' },
      this.publicKey,
      new TextEncoder().encode(text)
    );

    // Convert ArrayBuffer → Base64
    return btoa(String.fromCharCode(...new Uint8Array(encrypted)));
  }

  // Encrypt lists
  static async encryptList(
    list: any[],
    opts: { key?: string; keysToEncrypt?: string[] } = {}
  ) {
    const { key = '', keysToEncrypt = [] } = opts;

    for (let i = 0; i < list.length; i++) {
      list[i] = await this._encryptValue(key, list[i], keysToEncrypt);
    }
  }

  // Encrypt maps / objects
  static async encryptMap(
    map: Record<string, any>,
    opts: { keysToEncrypt?: string[] } = {}
  ) {
    const { keysToEncrypt = [] } = opts;

    for (const key of Object.keys(map)) {
      map[key] = await this._encryptValue(key, map[key], keysToEncrypt);
    }
  }

  // Internal dispatcher
  private static async _encryptValue(
    key: string,
    value: any,
    keysToEncrypt: string[]
  ): Promise<any> {
    if (value == null) return value;

    // String
    if (typeof value === 'string') {
      if (key && !keysToEncrypt.includes(key)) return value;

      return this.encryptString(value);
    }

    // Map
    if (typeof value === 'object' && !Array.isArray(value)) {
      await this.encryptMap(value, { keysToEncrypt });
      return value;
    }

    // List
    if (Array.isArray(value)) {
      await this.encryptList(value, { key, keysToEncrypt });
      return value;
    }

    return value;
  }
}
